import { first } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LazyAvatarGroup } from '../Avatar';
import { Button } from '../Button';
import { CommunicationMessage, IllustrationSize, Illustrations } from '../Communication';
import { ListSelectionType, VirtualizedList } from '../List';
import { Loader } from '../Loader';
import { ResponsiveDialog } from '../ResponsiveDialog/ResponsiveDialog';
import { SearchInput } from '../SearchInput';
import { ThemeMode, useProviderTheme } from '../hooks/useProviderTheme';
import './GroupPicker.css';

export interface GroupListItem {
  id: string;
  name: string;
  mailAddress?: string;
  content?: string;
  icon?: JSX.Element;
  disabled?: boolean;
}

interface GroupPickerLabels {
  title: string;
  searchPlaceholder: string;
  confirmBtn: string;
}

export enum GroupPickerSelectionMode {
  SingleSelection = 'singleSelection',
  MultiSelection = 'multiSelection',
}

export interface GroupPickerProps {
  open: boolean;
  groups: GroupListItem[];
  groupsSelected: (groups: GroupListItem[]) => void;
  canceled: () => void;
  isLoading: boolean;
  hasError?: boolean;
  onRetry?: () => void;
  /**
   * Overwrite default labels according to context (ie course is more
   * appropriate than "group").
   */
  labels?: GroupPickerLabels;
  selectionMode?: GroupPickerSelectionMode;
}

export const GroupPicker: React.FC<GroupPickerProps> = ({
  open,
  groupsSelected,
  groups,
  canceled,
  isLoading,
  hasError = false,
  onRetry,
  labels = null,
  selectionMode = GroupPickerSelectionMode.SingleSelection,
}) => {
  const { t } = useTranslation();
  const theme = useProviderTheme();
  const [selectedGroups, setSelectedGroups] = useState<GroupListItem[]>([]);
  const [filterQuery, setFilterQuery] = useState<string>('');

  // Set default labels
  const lbls = labels || {
    title: t('select_group', { defaultValue: 'Gruppe auswählen' }),
    searchPlaceholder: t('search_group', { defaultValue: 'Gruppen durchsuchen' }),
    confirmBtn: t('select_group', { defaultValue: 'Gruppe auswählen' }),
  };

  const lcFilterQuery = filterQuery.toLowerCase();

  const filteredGroups = groups.filter(
    (group) =>
      filterQuery === '' ||
      group.name?.toLowerCase().includes(lcFilterQuery) ||
      group.mailAddress?.toLowerCase().includes(lcFilterQuery) ||
      group.content?.toLocaleLowerCase().includes(lcFilterQuery),
  );

  const listItems = filteredGroups.map((group) => ({
    label: group.name,
    avatar: <LazyAvatarGroup groupId={group.id} name={group.name} disablePictureLoading={true} />,
    content: group.content ?? group.mailAddress,
    disabled: group.disabled,
  }));

  const handleSelectionChange = (selected: number[]) => {
    if (selectionMode === GroupPickerSelectionMode.SingleSelection) {
      const index = first(selected);

      if (index === undefined) {
        return;
      }

      setSelectedGroups([filteredGroups[index]]);
    } else {
      const selectedItems = selected.map((index) => filteredGroups[index]);
      setSelectedGroups(selectedItems);
    }
  };

  const selectedIndizes = useMemo(
    () =>
      selectedGroups
        .map((group) => filteredGroups.findIndex((a) => a.id === group.id))
        .filter((index) => index !== -1),
    [selectedGroups, filteredGroups],
  );

  return (
    <ResponsiveDialog
      open={open}
      header={lbls.title}
      className="groupPicker"
      primaryAction={
        <Button
          appearance="primary"
          disabled={isLoading || selectedGroups === null}
          loading={isLoading}
          onClick={() => selectedGroups !== null && groupsSelected(selectedGroups)}
          data-testid="selectGroupConfirmBtn"
        >
          {lbls.confirmBtn}
        </Button>
      }
      secondaryAction={
        <Button onClick={() => canceled()}>{t('cancel', { defaultValue: 'Abbrechen' })}</Button>
      }
    >
      <div className="flex flex-col items-stretch w-full h-full gap-2 wrapper">
        <SearchInput
          appearance="filled-darker"
          value={filterQuery}
          onChange={(_, data) => setFilterQuery(data?.value ?? '')}
          onClear={() => setFilterQuery('')}
          data-testid="searchInput"
          placeholder={lbls.searchPlaceholder}
        />
        {isLoading && (
          <div className="list flex items-center justify-center" data-testid="groupPickerLoader">
            <Loader />
          </div>
        )}
        {!isLoading && hasError && (
          <div className="list" data-testid="groupPickerError">
            <CommunicationMessage
              illustration={Illustrations.Error}
              illustrationSize={IllustrationSize.Small}
              headline={t('group_picker_error_headline', {
                defaultValue: 'Gruppen konnten nicht geladen werden',
              })}
              text=""
              primaryAction={
                onRetry
                  ? {
                      label: t('retry_loading', { defaultValue: 'Erneut laden' }),
                      onClick: onRetry,
                    }
                  : undefined
              }
            />
          </div>
        )}
        {!isLoading && !hasError && listItems.length > 0 && (
          <VirtualizedList
            entries={listItems}
            className="list"
            selectedEntries={selectedIndizes}
            selectionType={
              selectionMode === GroupPickerSelectionMode.SingleSelection
                ? ListSelectionType.Single
                : ListSelectionType.Multi
            }
            onSelectionChange={handleSelectionChange}
          />
        )}
        {!isLoading && !hasError && listItems.length === 0 && (
          <div className="list">
            <CommunicationMessage
              illustration={Illustrations.Empty}
              illustrationSize={IllustrationSize.Small}
              headline={t('group_picker_empty_hint', { defaultValue: 'Keine Gruppen vorhanden' })}
              text=""
              useDarkIllustration={theme.mode === ThemeMode.Dark}
            />
          </div>
        )}
      </div>
    </ResponsiveDialog>
  );
};
